import React, { useContext, useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Slide,
  TextField,
  MenuItem,
  Grid,
  Alert,
  AlertTitle,
  Stack,
  Tooltip,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { useFormik } from "formik";
import {
  CheckCircle,
  CloudDownload,
  DeleteOutline,
  Verified,
} from "@mui/icons-material";
import SpinnerContext from "../../context/spinner/SpinnerContext";
import SociosContext from "../../context/socios/SociosContext";
import moment from "moment";

import * as Constants from "../../support/Constants";
import * as Web3Manager from "../../support/Web3Manager";
import { CancelButton } from "../ui/CustomButtons";
import { UploadDocumentsModal } from "../UploadDocumentsModal/UploadDocumentsModal";
import Link from "@mui/material/Link";
import { useDocuments } from "../../hooks/useDocuments";
import { copyHash, shortHash } from "../utiles/hashUtils";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ModalAdherirCompromisoMobile = ({
  open,
  handleClose,
  compromiso,
  compromisoEditing,
}) => {
  const [disableButton, setDisableButton] = useState(false);
  const [documents, setDocuments] = useState([]);

  const { getFilesBatch } = useDocuments();
  const { openSpinner, closeSpinner } = useContext(SpinnerContext);
  const {
    adherirACompromiso,
    editarAdhesionCompromiso,
    eliminarAdhesionCompromiso,
    isLoading,
  } = useContext(SociosContext);

  const isEditing = compromisoEditing !== undefined;
  const [copiedIndex, setCopiedIndex] = useState(null);

  useEffect(() => {
    const loadExistingDocuments = async () => {
      try {
        if (!isEditing || !compromisoEditing?.documents?.length) {
          setDocuments([]);
          return;
        }

        const docsFromBackend = compromisoEditing.documents;

        const fileIds = docsFromBackend
          .map((d) => Number(d.fileId ?? d.id))
          .filter((id) => !Number.isNaN(id));

        if (!fileIds.length) {
          setDocuments(docsFromBackend);
          return;
        }

        const files = await getFilesBatch(fileIds);

        const fileMap = files.reduce((acc, f) => {
          acc[f.id] = f;
          return acc;
        }, {});      

        const merged = docsFromBackend.map((doc) => {
          const fileId = Number(doc.fileId ?? doc.id);
          const storageFile = fileMap[fileId];

          return {
            ...doc,
            id: fileId || doc.id,
            fileId: fileId || doc.fileId,
            name:
              storageFile?.name ||
              storageFile?.filename ||
              doc.name ||
              "Documento",
            url: storageFile?.url || doc.url || null,
            hash: doc.hash ?? storageFile?.hash ?? null,
          };
        });

        setDocuments(merged);
      } catch (e) {
        console.error(
          "Error loading existing documents for commitment (mobile):",
          e
        );
        setDocuments(compromisoEditing?.documents ?? []);
      }
    };

    loadExistingDocuments();
  }, [isEditing, compromisoEditing]);

  const initialState = {
    _socioType: Web3Manager.MEMBER_DATA.memberType,
    _socioCode: Web3Manager.MEMBER_DATA.memberCode,
    _commitmentCode: compromiso?.code,
    _status: Constants.HAS_INTENTION,
    _deadlineDate: moment().add(3, "months").format("YYYY-MM-DD"),
    _modificationDate: moment().format("YYYY-MM-DD"),
  };

  const getFormData = () => {
    if (!isEditing) return initialState;

    return {
      _socioType: Web3Manager.MEMBER_DATA.memberType,
      _socioCode: Web3Manager.MEMBER_DATA.memberCode,
      _commitmentCode: compromiso.code,
      _status: compromisoEditing.status,
      _deadlineDate:
        compromisoEditing.status === Constants.HAS_INTENTION
          ? moment(compromisoEditing.deadlineDate).format("YYYY-MM-DD")
          : moment().add(3, "months").format("YYYY-MM-DD"),
      _modificationDate: moment(compromisoEditing.modificationDate).format(
        "YYYY-MM-DD"
      ),
    };
  };

  const formAdherirCompromiso = useFormik({
    initialValues: getFormData(),
    validateOnChange: true,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      const {
        _socioType,
        _socioCode,
        _commitmentCode,
        _status,
        _deadlineDate,
        _modificationDate,
      } = values;

      let _deadlineDateParsed = moment(_deadlineDate, "YYYY-MM-DD").unix();
      const _modificationDateParsed = moment(
        _modificationDate,
        "YYYY-MM-DD"
      ).unix();

      if (_status === Constants.COMPLIES) {
        _deadlineDateParsed = 0;
      }

      const commitmentDocuments = documents
        .map((d) => {
          const fileId = d.fileId ?? d.id;
          if (!fileId) return null;

          return {
            hash: d?.hash ?? null,
            fileId,
          };
        })
        .filter(Boolean);

      setDisableButton(true);
      openSpinner();

      try {
        let response;

        if (!isEditing) {
          response = await adherirACompromiso(
            _socioType,
            _socioCode,
            _commitmentCode,
            _status,
            _deadlineDateParsed,
            _modificationDateParsed,
            commitmentDocuments
          );
        } else if (_status === Constants.REMOVE_COMMITMENT) {
          response = await eliminarAdhesionCompromiso(
            _socioCode,
            _socioType,
            _commitmentCode
          );
        } else {
          response = await editarAdhesionCompromiso(
            _socioType,
            _socioCode,
            _commitmentCode,
            _status,
            _deadlineDateParsed,
            _modificationDateParsed,
            commitmentDocuments
          );
        }

        setDisableButton(false);
        resetForm();
        handleClose(false, response, true);
      } catch (error) {
        console.error(
          "Error adhiriendo/editar compromiso (mobile):",
          error
        );
        setDisableButton(false);
        handleClose(false, error, false);
      } finally {
        closeSpinner();
      }
    },
  });

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    values,
    resetForm,
  } = formAdherirCompromiso;

  const onUploadDocumentsSuccess = (newDocs = []) => {
    const mapped = newDocs.map((d) => ({
      ...d,
      id: d.id,
      fileId: d.id,
      name: d.filename || d.name,
      hash: d.hash ?? null,
    }));
    setDocuments((prev) => [...prev, ...mapped]);
  };

  const handleDeleteDoc = async (docId) => {
    try {
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={() => {
        resetForm();
        handleClose(true, null, false);
      }}
      TransitionComponent={Transition}
    >
      <AppBar sx={{ position: "relative" }}>
        <Toolbar>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            {!isEditing
              ? "Adherir al compromiso"
              : "Editar adhesión al compromiso"}
          </Typography>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => {
              resetForm();
              handleClose(true, null, false);
            }}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }} component="form" onSubmit={handleSubmit}>
        <Grid container direction="column" spacing={2}>
          <Grid item xs={12}>
            <TextField
              select
              name="_status"
              label="Estado del compromiso"
              value={values._status}
              onChange={handleChange}
              onBlur={handleBlur}
              fullWidth
              size="small"
              variant="outlined"
            >
              <MenuItem value={Constants.COMPLIES}>Cumple</MenuItem>
              <MenuItem value={Constants.HAS_INTENTION}>
                Tiene intención
              </MenuItem>
              {isEditing && (
                <MenuItem value={Constants.REMOVE_COMMITMENT}>
                  Eliminar adhesión
                </MenuItem>
              )}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            {values._status === Constants.HAS_INTENTION && (
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  label="Fecha límite"
                  inputFormat="DD-MM-YYYY"
                  value={values._deadlineDate}
                  onChange={(newValue) => {
                    formAdherirCompromiso.setFieldValue(
                      "_deadlineDate",
                      moment(newValue).format("YYYY-MM-DD")
                    );
                  }}
                  disablePast
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      variant="outlined"
                      size="small"
                    />
                  )}
                />
              </LocalizationProvider>
            )}
          </Grid>
          {!isEditing && (
            <Grid item xs={12} mt={3} mb={2}>
              <Alert severity="info" sx={{ fontSize: "12px" }}>
                <AlertTitle>
                  ¿Está seguro que desea adherirse al compromiso{" "}
                  <strong>{compromiso.title}</strong>?
                </AlertTitle>
              </Alert>
            </Grid>
          )}

          <Grid item xs={12} mt={2} mb={2}>
            <Stack direction="row" alignItems="center" gap={2}>
              <Typography variant="subtitle1" fontWeight={600}>
                Documentos
              </Typography>
              <UploadDocumentsModal
                openButton={
                  <Button variant="contained" sx={{ textTransform: "none" }}>
                    Agregar documento
                  </Button>
                }
                onSave={onUploadDocumentsSuccess}
              />
            </Stack>

            {documents.length > 0 ? (
              <Stack mt={2} gap={1}>
                {documents.map((doc, index) => {
                  const key =
                    doc.fileId ?? doc.id ?? doc.url ?? index;

                  return (
                    <Stack
                      key={key}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        border: "1px solid #e0e0e0",
                        borderRadius: "6px",
                        p: 1,
                      }}
                    >
                      <Stack direction="row" alignItems="center" gap={1}>
                        <Typography fontWeight={500}>
                          {doc.name || doc.fileName || "Documento"}
                        </Typography>

                        {doc.hash && (
                          <Tooltip
                            title={
                              copiedIndex === index
                                ? "¡Copiado!"
                                : `SHA-256: ${doc.hash}`
                            }
                          >
                            <Chip
                              size="small"
                              label={
                                copiedIndex === index
                                  ? "¡Copiado!"
                                  : `SHA-256: ${shortHash(doc.hash)}`
                              }
                              variant="outlined"
                              onClick={() =>
                                copyHash(doc.hash, setCopiedIndex, index)
                              }
                              sx={{ cursor: "pointer" }}
                            />
                          </Tooltip>
                        )}

                        {doc.txHash && (
                          <Tooltip title="Verificado en blockchain">
                            <Verified fontSize="small" color="success" />
                          </Tooltip>
                        )}
                      </Stack>

                      <Stack direction="row" gap={1}>
                        {doc.url && (
                          <Tooltip title="Descargar">
                            <IconButton
                              component={Link}
                              href={doc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              size="small"
                            >
                              <CloudDownload fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        <Tooltip title="Eliminar">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteDoc(doc.id)}
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  );
                })}
              </Stack>
            ) : (
              <Typography mt={1} color="text.secondary">
                Aún no hay documentos adjuntos
              </Typography>
            )}
          </Grid>

          <Box
            mt={2}
            sx={{ justifyContent: "flex-end", display: "flex", gap: "10px" }}
          >
            <Button
              variant="contained"
              color="primary"
              type="submit"
              endIcon={<CheckCircle />}
              onClick={handleSubmit}
              disabled={isLoading || disableButton}
            >
              Confirmar
            </Button>
            <CancelButton
              label="Cancelar"
              onClick={() => {
                resetForm();
                handleClose(true, null, false);
              }}
              disabled={isLoading}
            />
          </Box>
        </Grid>
      </Box>
    </Dialog>
  );
};

export default ModalAdherirCompromisoMobile;
